import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Sidebar from "@/components/layout/Sidebar";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MessageCircle, Send, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = () => {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [activeTab, setActiveTab] = useState("rooms");
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [draft, setDraft] = useState("");
  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  const typingTimersRef = useRef({});
  const typingDebounceRef = useRef(null);
  const typingSentRef = useRef(false);

  const meId = user?.id;
  const selectedRoomId = selectedRoom?.id;
  const selectedUserId = selectedUser?.id;
  const usersById = useMemo(() => {
    const map = {};
    for (const u of users) map[u.id] = u;
    return map;
  }, [users]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 10);
  }, []);

  const appendUniqueMessage = useCallback((message) => {
    if (!message?.id) {
      setMessages((prev) => [...prev, message]);
      return;
    }
    setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
  }, []);

  const clearTypingUsers = useCallback(() => {
    const timers = typingTimersRef.current;
    Object.values(timers).forEach((t) => clearTimeout(t));
    typingTimersRef.current = {};
    setTypingUsers({});
  }, []);

  const emitTyping = useCallback((isTyping) => {
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    let payload = null;
    if (activeTab === "rooms" && selectedRoomId) {
      payload = {
        type: "typing",
        context: "room",
        room_id: selectedRoomId,
        is_typing: isTyping,
      };
    } else if (activeTab === "inbox" && selectedUserId) {
      payload = {
        type: "typing",
        context: "direct",
        recipient_id: selectedUserId,
        is_typing: isTyping,
      };
    }

    if (payload) {
      socket.send(JSON.stringify(payload));
    }
  }, [activeTab, selectedRoomId, selectedUserId]);

  const loadInitial = useCallback(async () => {
    try {
      const [roomsRes, usersRes, inboxRes] = await Promise.all([
        axios.get(`${API}/chat/rooms`),
        axios.get(`${API}/chat/users`),
        axios.get(`${API}/chat/inbox`),
      ]);
      setRooms(roomsRes.data || []);
      setUsers((usersRes.data || []).filter((u) => u.id !== meId));
      setInbox(inboxRes.data || []);
      setSelectedRoom((current) => current || roomsRes.data?.[0] || null);
    } catch (error) {
      toast.error("Failed to load chat data");
    }
  }, [meId]);

  const loadRoomMessages = useCallback(async (roomId) => {
    if (!roomId) return;
    try {
      const response = await axios.get(`${API}/chat/rooms/${roomId}/messages`);
      setMessages(response.data || []);
      scrollToBottom();
    } catch (error) {
      toast.error("Failed to load room messages");
    }
  }, [scrollToBottom]);

  const loadDirectMessages = useCallback(async (otherUserId) => {
    if (!otherUserId) return;
    try {
      const response = await axios.get(`${API}/chat/direct/${otherUserId}/messages`);
      setMessages(response.data || []);
      const refreshedInbox = await axios.get(`${API}/chat/inbox`);
      setInbox(refreshedInbox.data || []);
      scrollToBottom();
    } catch (error) {
      toast.error("Failed to load private messages");
    }
  }, [scrollToBottom]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    if (activeTab === "rooms") {
      loadRoomMessages(selectedRoomId);
    } else if (activeTab === "inbox" && selectedUserId) {
      loadDirectMessages(selectedUserId);
    }
  }, [activeTab, loadDirectMessages, loadRoomMessages, selectedRoomId, selectedUserId]);

  useEffect(() => {
    const token = localStorage.getItem("eagle_token");
    if (!token) return;
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsHost = process.env.REACT_APP_BACKEND_URL
      ? process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, wsProtocol).replace(/\/$/, "")
      : `${wsProtocol}//${window.location.host}`;
    const socket = new WebSocket(`${wsHost}/api/ws/chat?token=${encodeURIComponent(token)}`);
    wsRef.current = socket;

    socket.onmessage = (event) => {
      if (event.data === "pong") return;
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === "room_message") {
          if (activeTab === "rooms" && selectedRoomId === payload.room_id) {
            appendUniqueMessage(payload.message);
            scrollToBottom();
          } else {
            toast.info(`New message in ${rooms.find((r) => r.id === payload.room_id)?.name || "room"}`);
          }
        }
        if (payload.type === "direct_message") {
          const msg = payload.message;
          const isCurrentThread =
            activeTab === "inbox" &&
            selectedUserId &&
            (msg.sender_id === selectedUserId || msg.recipient_id === selectedUserId);
          if (isCurrentThread) {
            appendUniqueMessage(msg);
            scrollToBottom();
          } else if (msg.sender_id !== meId) {
            toast.info(`New private message from ${msg.sender_name}`);
          }
          axios.get(`${API}/chat/inbox`).then((res) => setInbox(res.data || []));
        }
        if (payload.type === "typing" && payload.user_id !== meId) {
          const roomMatch =
            payload.context === "room" &&
            activeTab === "rooms" &&
            selectedRoomId === payload.room_id;
          const directMatch =
            payload.context === "direct" &&
            activeTab === "inbox" &&
            selectedUserId === payload.user_id;

          if (!(roomMatch || directMatch)) return;

          if (!payload.is_typing) {
            setTypingUsers((prev) => {
              const next = { ...prev };
              delete next[payload.user_id];
              return next;
            });
            if (typingTimersRef.current[payload.user_id]) {
              clearTimeout(typingTimersRef.current[payload.user_id]);
              delete typingTimersRef.current[payload.user_id];
            }
            return;
          }

          setTypingUsers((prev) => ({
            ...prev,
            [payload.user_id]: payload.user_name || usersById[payload.user_id]?.name || "User",
          }));
          if (typingTimersRef.current[payload.user_id]) {
            clearTimeout(typingTimersRef.current[payload.user_id]);
          }
          typingTimersRef.current[payload.user_id] = setTimeout(() => {
            setTypingUsers((prev) => {
              const next = { ...prev };
              delete next[payload.user_id];
              return next;
            });
            delete typingTimersRef.current[payload.user_id];
          }, 3000);
        }
      } catch (_) {
        // ignore malformed frame
      }
    };

    const ping = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) socket.send("ping");
    }, 25000);

    return () => {
      clearInterval(ping);
      clearTypingUsers();
      socket.close();
    };
  }, [activeTab, appendUniqueMessage, clearTypingUsers, meId, rooms, scrollToBottom, selectedRoomId, selectedUserId, usersById]);

  useEffect(() => {
    if (typingSentRef.current) {
      emitTyping(false);
    }
    typingSentRef.current = false;
    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current);
      typingDebounceRef.current = null;
    }
    clearTypingUsers();
  }, [activeTab, clearTypingUsers, emitTyping, selectedRoomId, selectedUserId]);

  const sendMessage = async () => {
    const content = draft.trim();
    if (!content) return;
    try {
      let response = null;
      if (activeTab === "rooms" && selectedRoom?.id) {
        response = await axios.post(`${API}/chat/rooms/${selectedRoom.id}/messages`, { content });
      } else if (activeTab === "inbox" && selectedUser?.id) {
        response = await axios.post(`${API}/chat/direct/${selectedUser.id}`, { content });
      }

      const sentMessage = response?.data;
      if (sentMessage) {
        appendUniqueMessage(sentMessage);
        scrollToBottom();
      }
      if (typingSentRef.current) {
        emitTyping(false);
        typingSentRef.current = false;
      }
      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
        typingDebounceRef.current = null;
      }
      setDraft("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    }
  };

  const handleDraftChange = (value) => {
    setDraft(value);

    const hasText = value.trim().length > 0;
    if (!hasText) {
      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
        typingDebounceRef.current = null;
      }
      if (typingSentRef.current) {
        emitTyping(false);
        typingSentRef.current = false;
      }
      return;
    }

    if (!typingSentRef.current) {
      emitTyping(true);
      typingSentRef.current = true;
    }

    if (typingDebounceRef.current) {
      clearTimeout(typingDebounceRef.current);
    }
    typingDebounceRef.current = setTimeout(() => {
      emitTyping(false);
      typingSentRef.current = false;
      typingDebounceRef.current = null;
    }, 1200);
  };

  const typingLabel = useMemo(() => {
    const names = Object.values(typingUsers);
    if (names.length === 0) return "";
    if (names.length === 1) return `${names[0]} is typing...`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing...`;
    return `${names.length} people are typing...`;
  }, [typingUsers]);

  const getMessageAuthor = (msg) => {
    if (msg.sender_id === meId) return "You";
    return msg.sender_name || usersById[msg.sender_id]?.name || "User";
  };

  const getUserInitials = (name) =>
    (name || "U")
      .split(" ")
      .slice(0, 2)
      .map((v) => v[0]?.toUpperCase() || "")
      .join("");

  return (
    <div className="min-h-screen bg-background">
      <Sidebar role={user?.role} />
      <main className="lg:ml-64 p-4 lg:p-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          <Card className="xl:col-span-1">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Chats
              </CardTitle>
              <CardDescription>Room channels and private inbox</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="rooms">Rooms</TabsTrigger>
                  <TabsTrigger value="inbox">Inbox</TabsTrigger>
                </TabsList>

                <TabsContent value="rooms" className="mt-4">
                  <ScrollArea className="h-[520px] pr-2">
                    <div className="space-y-2">
                      {rooms.map((room) => (
                        <button
                          key={room.id}
                          className={`w-full text-left p-3 rounded-lg border transition ${
                            selectedRoom?.id === room.id ? "border-primary bg-primary/10" : "border-border hover:bg-muted/50"
                          }`}
                          onClick={() => {
                            setSelectedRoom(room);
                            setSelectedUser(null);
                            setActiveTab("rooms");
                          }}
                        >
                          <p className="font-medium">{room.name}</p>
                          <p className="text-xs text-muted-foreground line-clamp-2">{room.description}</p>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="inbox" className="mt-4">
                  <ScrollArea className="h-[250px] pr-2 mb-3">
                    <div className="space-y-2">
                      {inbox.map((thread) => {
                        const other = usersById[thread.other_user_id];
                        return (
                          <button
                            key={thread.thread_key}
                            className={`w-full text-left p-3 rounded-lg border transition ${
                              selectedUser?.id === thread.other_user_id ? "border-primary bg-primary/10" : "border-border hover:bg-muted/50"
                            }`}
                            onClick={() => {
                              if (other) {
                                setSelectedUser(other);
                                setSelectedRoom(null);
                                setActiveTab("inbox");
                              }
                            }}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium truncate">{other?.name || thread.other_user_id}</p>
                              {thread.unread_count > 0 && <Badge>{thread.unread_count}</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{thread.last_message}</p>
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>

                  <p className="text-xs uppercase text-muted-foreground mb-2">Start private message</p>
                  <ScrollArea className="h-[250px] pr-2">
                    <div className="space-y-2">
                      {users.map((u) => (
                        <button
                          key={u.id}
                          className={`w-full text-left p-2 rounded-lg border transition ${
                            selectedUser?.id === u.id ? "border-primary bg-primary/10" : "border-border hover:bg-muted/50"
                          }`}
                          onClick={() => {
                            setSelectedUser(u);
                            setSelectedRoom(null);
                            setActiveTab("inbox");
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback>{getUserInitials(u.name)}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{u.name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {u.role} {u.company ? `• ${u.company}` : ""}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <Card className="xl:col-span-3">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-primary" />
                {activeTab === "rooms" ? selectedRoom?.name || "Select a room" : selectedUser?.name || "Select a user"}
              </CardTitle>
              <CardDescription>
                {activeTab === "rooms"
                  ? selectedRoom?.description || "Room chat"
                  : "Private direct messages"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ScrollArea className="h-[540px] rounded-lg border border-border bg-muted/20 p-4">
                <div className="space-y-3">
                  {messages.map((msg) => {
                    const mine = msg.sender_id === meId;
                    return (
                      <div key={msg.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-xl px-3 py-2 ${mine ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                          <p className={`text-xs mb-1 ${mine ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                            {getMessageAuthor(msg)}
                          </p>
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                          <p className={`text-[10px] mt-1 ${mine ? "text-primary-foreground/70" : "text-muted-foreground/80"}`}>
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
              </ScrollArea>

              <div className="flex gap-2">
                <Input
                  placeholder="Type a message..."
                  value={draft}
                  onChange={(e) => handleDraftChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <Button onClick={sendMessage}>
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </Button>
              </div>
              <p className="text-xs text-muted-foreground min-h-[16px]">{typingLabel}</p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ChatPage;
